import { Controller } from "@hotwired/stimulus"

/**
 * ReservationChangeController
 *
 * Handles the unified reservation change form that supports both
 * date modifications and rental transfers. Updates pricing preview
 * and available rentals via AJAX when dates or rental selection changes.
 */
export default class extends Controller {
  static targets = [
    "checkIn",
    "checkOut",
    "rental",
    "preview",
    "rateLockIndicator",
    "nights",
    "basePrice",
    "cleaningFee",
    "cleaningFeeRow",
    "tax",
    "newTotal",
    "priceDifference",
    "chargeWarning",
    "refundNote",
    "noChangeNote",
    "waivedNote",
    "waiveCheckbox",
    "unavailabilityBanner",
    "submit",
    "error"
  ]

  static values = {
    reservationId: Number,
    currentRentalId: Number
  }

  connect() {
    // Store original rental options for reference
    this.originalRentalId = this.currentRentalIdValue
    // Store last pricing data for waive toggle
    this.lastPricingData = null

    // M-7: AbortController for the in-flight pricing request.
    // Each new fetch cancels the previous one so a late-arriving response from
    // an earlier (stale) input combination can't overwrite the current preview.
    this._pricingAbortController = null

    // M-7: Debounce timer for date input changes. Coalesces rapid edits
    // (e.g. operator clears check-in and types a new value) before firing a fetch.
    this._dateDebounceTimer = null

    // Fetch initial pricing on connect
    this.updatePricing()
  }

  /**
   * Called when check-in or check-out date changes.
   * Updates both available rentals and pricing.
   *
   * M-7 race prevention: debounced 150ms so rapid sequential edits (e.g. operator
   * clears the check-in field and types a new date) coalesce into a single fetch.
   * Pairs with AbortController in fetchPricing so any in-flight request for a
   * superseded input combination is cancelled before the new one fires.
   */
  updateDates(event) {
    if (event) {
      event.preventDefault()
    }

    // Coalesce rapid edits before hitting the server
    clearTimeout(this._dateDebounceTimer)
    this._dateDebounceTimer = setTimeout(() => {
      this._performUpdateDates()
    }, 150)
  }

  _performUpdateDates() {
    const checkInValue = this.hasCheckInTarget ? this.checkInTarget.value : null
    const checkOutValue = this.hasCheckOutTarget ? this.checkOutTarget.value : null

    if (!checkInValue || !checkOutValue) {
      this.clearPreview()
      return
    }

    const checkIn = this.parseDate(checkInValue)
    const checkOut = this.parseDate(checkOutValue)

    if (!checkIn || !checkOut || checkOut <= checkIn) {
      this.showError('Check-out date must be after check-in date')
      return
    }

    // First update available rentals for new dates, then update pricing
    this.fetchAvailableRentals(checkIn, checkOut)
  }

  /**
   * Called when rental selection changes (pricing only, rentals already loaded)
   */
  updatePricing(event) {
    if (event) {
      event.preventDefault()
    }

    const checkInValue = this.hasCheckInTarget ? this.checkInTarget.value : null
    const checkOutValue = this.hasCheckOutTarget ? this.checkOutTarget.value : null
    const rentalId = this.hasRentalTarget ? this.rentalTarget.value : null

    if (!checkInValue || !checkOutValue) {
      this.clearPreview()
      return
    }

    const checkIn = this.parseDate(checkInValue)
    const checkOut = this.parseDate(checkOutValue)

    if (!checkIn || !checkOut || checkOut <= checkIn) {
      this.showError('Check-out date must be after check-in date')
      return
    }

    this.fetchPricing(checkIn, checkOut, rentalId)
  }

  /**
   * Parse date string into Date object
   * Supports ISO format (YYYY-MM-DD) and MM/DD/YYYY format
   */
  parseDate(dateString) {
    if (!dateString) return null

    try {
      // ISO format: YYYY-MM-DD
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number)
        return new Date(year, month - 1, day)
      }

      // US format: MM/DD/YYYY
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
        const [month, day, year] = dateString.split('/').map(Number)
        return new Date(year, month - 1, day)
      }

      console.error(`Failed to parse date: ${dateString}`)
      return null
    } catch (e) {
      console.error(`Error parsing date ${dateString}:`, e)
      return null
    }
  }

  /**
   * Format Date object as YYYY-MM-DD string for API requests
   */
  formatDate(date) {
    if (!date) return ''

    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
  }

  /**
   * Fetch available rentals for the specified dates
   */
  async fetchAvailableRentals(checkIn, checkOut) {
    if (!this.reservationIdValue) {
      console.error('No reservation ID available')
      return
    }

    const url = `/admin/reservations/${this.reservationIdValue}/change_available_rentals`
    const params = new URLSearchParams({
      new_check_in: this.formatDate(checkIn),
      new_check_out: this.formatDate(checkOut)
    })

    try {
      const response = await fetch(`${url}?${params}`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to fetch available rentals')
      }

      const data = await response.json()
      this.updateRentalDropdown(data.active_rentals, data.backup_rentals)

      // After updating rentals, fetch pricing for currently selected rental
      const rentalId = this.hasRentalTarget ? this.rentalTarget.value : null
      this.fetchPricing(checkIn, checkOut, rentalId)
    } catch (error) {
      console.error('Error fetching available rentals:', error)
      this.showError(error.message || 'Failed to check rental availability. Please try again.')
    }
  }

  /**
   * Update the rental dropdown with grouped availability information
   * @param {Array} activeRentals - Array of active (available) rentals
   * @param {Array} backupRentals - Array of backup (inactive) rentals
   */
  updateRentalDropdown(activeRentals, backupRentals) {
    if (!this.hasRentalTarget) return

    const currentSelection = this.rentalTarget.value
    const dropdown = this.rentalTarget

    // Clear existing options
    dropdown.innerHTML = ''

    // Helper function to create option element
    const createOption = (rental) => {
      const option = document.createElement('option')
      option.value = rental.id

      // Use server-supplied display_name (already disambiguated) when available,
      // falling back to constructing from name + campsite_type for older responses.
      let label = rental.display_name || `${rental.name} (${rental.campsite_type})`
      if (rental.is_current) {
        label += ' - Current'
      }
      if (!rental.available) {
        label += ' - UNAVAILABLE'
      }

      option.textContent = label
      option.disabled = !rental.available
      option.dataset.price = rental.price
      option.dataset.isCurrent = rental.is_current
      option.dataset.available = rental.available
      option.dataset.status = rental.status

      if (!rental.available) {
        option.style.color = '#9CA3AF' // gray-400
      }

      return option
    }

    // Add "Available Rentals" optgroup
    if (activeRentals && activeRentals.length > 0) {
      const activeGroup = document.createElement('optgroup')
      activeGroup.label = 'Available Rentals'
      activeRentals.forEach(rental => {
        activeGroup.appendChild(createOption(rental))
      })
      dropdown.appendChild(activeGroup)
    }

    // Add "Backup Rentals" optgroup (only if there are backup rentals)
    if (backupRentals && backupRentals.length > 0) {
      const backupGroup = document.createElement('optgroup')
      backupGroup.label = 'Backup Rentals'
      backupRentals.forEach(rental => {
        backupGroup.appendChild(createOption(rental))
      })
      dropdown.appendChild(backupGroup)
    }

    // Try to restore previous selection if still available
    const previousOption = dropdown.querySelector(`option[value="${currentSelection}"]`)
    if (previousOption && !previousOption.disabled) {
      dropdown.value = currentSelection
    } else {
      // Default to original rental if current selection is unavailable
      const originalOption = dropdown.querySelector(`option[value="${this.originalRentalId}"]`)
      if (originalOption && !originalOption.disabled) {
        dropdown.value = this.originalRentalId
      }
    }
  }

  /**
   * Fetch pricing preview from server.
   *
   * M-7 cancellation: each call aborts the previous in-flight request via
   * AbortController. If the operator changes both check-in and check-out in rapid
   * succession, the first fetch (with the intermediate input state) is cancelled
   * before its response can overwrite the preview built from the final inputs.
   * AbortError is silently swallowed — it is expected and not an operator-visible error.
   */
  async fetchPricing(checkIn, checkOut, rentalId) {
    if (!this.reservationIdValue) {
      console.error('No reservation ID available')
      return
    }

    // Cancel any in-flight request for a superseded input combination
    if (this._pricingAbortController) {
      this._pricingAbortController.abort()
    }
    this._pricingAbortController = new AbortController()
    const signal = this._pricingAbortController.signal

    const url = `/admin/reservations/${this.reservationIdValue}/change_pricing_preview`
    const params = new URLSearchParams({
      new_check_in: this.formatDate(checkIn),
      new_check_out: this.formatDate(checkOut),
      target_rental_id: rentalId || ''
    })

    try {
      const response = await fetch(`${url}?${params}`, {
        signal,
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken
        }
      })

      // M-10: read server's `error` key before falling back to the generic message.
      // Covers cases like missing pricing snapshot (422), invalid rental (404), etc.
      // Logical unavailability (past dates, conflicts) comes back as 200 with
      // available: false + unavailability_reason and is handled by displayPricing.
      if (!response.ok) {
        let errorMessage = 'Failed to calculate pricing. Please try again.'
        try {
          const errorData = await response.json()
          if (errorData.error) errorMessage = errorData.error
        } catch (_e) { /* response was not JSON (e.g. 500 HTML page) */ }
        this.showError(errorMessage)
        return
      }

      const data = await response.json()
      this.displayPricing(data)
    } catch (error) {
      // AbortError is expected when a newer fetch cancels this one — not operator-visible
      if (error.name === 'AbortError') return
      console.error('Error fetching pricing:', error)
      this.showError(error.message || 'Failed to calculate pricing. Please try again.')
    }
  }

  /**
   * Toggle waive price difference and update display
   */
  toggleWaive(event) {
    if (this.lastPricingData) {
      this.displayPricing(this.lastPricingData)
    }
  }

  /**
   * Update the UI with pricing data from the server
   */
  displayPricing(data) {
    // Store pricing data for waive toggle
    this.lastPricingData = data

    // Check if waive is enabled
    const waiveEnabled = this.hasWaiveCheckboxTarget && this.waiveCheckboxTarget.checked

    // Show/hide unavailability banner and gate the submit button
    this.updateAvailabilityBanner(data.available, data.unavailability_reason)

    // Show the preview section
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.remove('hidden')
    }

    // Hide any previous errors
    this.hideError()

    // Update rate lock indicator
    if (this.hasRateLockIndicatorTarget) {
      if (data.rate_locked) {
        this.rateLockIndicatorTarget.textContent = 'Rate-Locked'
        this.rateLockIndicatorTarget.className = 'inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset bg-blue-50 text-blue-700 ring-blue-700/10'
      } else {
        this.rateLockIndicatorTarget.textContent = 'Current Rates'
        this.rateLockIndicatorTarget.className = 'inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset bg-amber-50 text-amber-700 ring-amber-700/10'
      }
    }

    // Update pricing details
    if (this.hasNightsTarget) {
      this.nightsTarget.textContent = data.nights
    }

    if (this.hasBasePriceTarget) {
      this.basePriceTarget.textContent = this.formatCurrency(data.base_price)
    }

    // Handle cleaning fee (may be 0 or null)
    if (this.hasCleaningFeeTarget && this.hasCleaningFeeRowTarget) {
      if (data.cleaning_fee && data.cleaning_fee > 0) {
        this.cleaningFeeTarget.textContent = this.formatCurrency(data.cleaning_fee)
        this.cleaningFeeRowTarget.classList.remove('hidden')
      } else {
        this.cleaningFeeRowTarget.classList.add('hidden')
      }
    }

    if (this.hasTaxTarget) {
      this.taxTarget.textContent = this.formatCurrency(data.tax)
    }

    if (this.hasNewTotalTarget) {
      this.newTotalTarget.textContent = this.formatCurrency(data.new_total)
    }

    // Calculate and display price difference (respecting waive checkbox)
    const priceDifference = data.price_difference
    const displayDifference = waiveEnabled ? 0 : priceDifference

    if (this.hasPriceDifferenceTarget) {
      if (waiveEnabled) {
        this.priceDifferenceTarget.textContent = `${this.formatCurrency(0)} (waived)`
        this.priceDifferenceTarget.className = 'text-lg font-bold text-gray-500'
      } else if (priceDifference > 0) {
        this.priceDifferenceTarget.textContent = `+${this.formatCurrency(priceDifference)}`
        this.priceDifferenceTarget.className = 'text-lg font-bold text-red-600'
      } else if (priceDifference < 0) {
        this.priceDifferenceTarget.textContent = `-${this.formatCurrency(Math.abs(priceDifference))}`
        this.priceDifferenceTarget.className = 'text-lg font-bold text-green-600'
      } else {
        this.priceDifferenceTarget.textContent = this.formatCurrency(0)
        this.priceDifferenceTarget.className = 'text-lg font-bold text-gray-900'
      }
    }

    // Show/hide appropriate messages (respecting waive)
    this.updateMessages(displayDifference, waiveEnabled)
  }

  /**
   * Show/hide charge warning, refund note, or no change note
   */
  updateMessages(priceDifference, waiveEnabled = false) {
    // Hide all messages first
    if (this.hasChargeWarningTarget) {
      this.chargeWarningTarget.classList.add('hidden')
    }
    if (this.hasRefundNoteTarget) {
      this.refundNoteTarget.classList.add('hidden')
    }
    if (this.hasNoChangeNoteTarget) {
      this.noChangeNoteTarget.classList.add('hidden')
    }
    if (this.hasWaivedNoteTarget) {
      this.waivedNoteTarget.classList.add('hidden')
    }

    // Show appropriate message
    if (waiveEnabled) {
      if (this.hasWaivedNoteTarget) {
        this.waivedNoteTarget.classList.remove('hidden')
      }
    } else if (priceDifference > 0) {
      if (this.hasChargeWarningTarget) {
        this.chargeWarningTarget.classList.remove('hidden')
      }
    } else if (priceDifference < 0) {
      if (this.hasRefundNoteTarget) {
        this.refundNoteTarget.classList.remove('hidden')
      }
    } else {
      if (this.hasNoChangeNoteTarget) {
        this.noChangeNoteTarget.classList.remove('hidden')
      }
    }
  }

  /**
   * Format number as USD currency
   */
  formatCurrency(amount) {
    if (amount === null || amount === undefined) {
      return '-'
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  /**
   * Show or hide the unavailability banner and disable/enable the submit button.
   */
  updateAvailabilityBanner(available, reason) {
    const unavailable = available === false

    if (this.hasUnavailabilityBannerTarget) {
      if (unavailable) {
        const message = reason || 'This rental is not available for the selected dates.'
        this.unavailabilityBannerTarget.innerHTML = `
          <div class="flex items-center gap-2">
            <i class="ph ph-warning-circle h-4 w-4 flex-shrink-0"></i>
            <span>${this.escapeHtml(message)}</span>
          </div>
        `
        this.unavailabilityBannerTarget.classList.remove('hidden')
      } else {
        this.unavailabilityBannerTarget.classList.add('hidden')
        this.unavailabilityBannerTarget.innerHTML = ''
      }
    }

    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = unavailable
    }
  }

  /**
   * Clear the pricing preview section.
   * S2: also cancel any in-flight fetch so a late-arriving response cannot
   * re-show the preview after the operator clears a date field.
   */
  clearPreview() {
    if (this._pricingAbortController) {
      this._pricingAbortController.abort()
      this._pricingAbortController = null
    }

    if (this.hasPreviewTarget) {
      this.previewTarget.classList.add('hidden')
    }
    // Re-enable submit when preview is cleared (dates/rental not yet selected)
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = false
    }
  }

  /**
   * Display an error message
   */
  showError(message) {
    this.clearPreview()

    if (this.hasErrorTarget) {
      this.errorTarget.classList.remove('hidden')
      this.errorTarget.innerHTML = `
        <div class="rounded-md bg-red-50 p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <i class="ph ph-x-circle h-5 w-5 text-red-400"></i>
            </div>
            <div class="ml-3">
              <p class="text-sm font-medium text-red-800">${this.escapeHtml(message)}</p>
            </div>
          </div>
        </div>
      `
    }
  }

  /**
   * Hide the error display
   */
  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('hidden')
      this.errorTarget.innerHTML = ''
    }
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  /**
   * Get CSRF token from meta tag
   */
  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ''
  }
}
